import React, { useEffect, useState } from "react";
import {
  EuiBadge,
  EuiButton,
  EuiCallOut,
  EuiCode,
  EuiDescriptionList,
  EuiFlexGroup,
  EuiFlexItem,
  EuiLoadingSpinner,
  EuiPanel,
  EuiSpacer,
  EuiTabbedContent,
  EuiTabbedContentTab,
  EuiText,
  EuiTitle,
} from "@elastic/eui";
import { useApi } from "../";
import { useAnketaAuth } from "../auth/context";
import { TableSkeleton } from "src/components/skeleton";

interface ReplyAddress {
  value: string;
  source: string;
}

interface MailTransport {
  mode: "graph" | "smtp";
  fromHeader: string;
  senderDisplay: string;
  sender: string;
  senderSource: "GRAPH_SENDER" | "NODE_MAILER_FROM" | "NODE_MAILER_USERNAME";
  fromMatchesSender: boolean;
  replyTo: ReplyAddress;
  ticketReplyTo: ReplyAddress;
  endpoint: string;
  encryption?: string;
  pinned: boolean;
}

interface MailTemplateSummary {
  id: string;
  name: string;
  description: string;
  trigger: string;
  subject: string;
  replyTo: string;
}

interface MailSetupResponse {
  transport: MailTransport;
  recipient: string | null;
  canSend: boolean;
  templates: MailTemplateSummary[];
}

/** Unwraps the AnketaApiResponse envelope; null whenever anything went wrong. */
const payloadOf = <T,>(resp: any): T | null =>
  // useApi never throws on an HTTP error - it returns success:false - so the
  // check has to be explicit; a try/catch here would be dead code.
  resp?.success ? ((resp?.data as any)?.data ?? null) : null;

/**
 * One template tab: renders the mail in an isolated iframe and offers to send
 * it to the logged-in admin.
 *
 * The iframe is deliberate. Mail HTML is table soup with its own <style>, and
 * dropping it into the page would let it restyle the admin UI around it - and
 * would show it under EUI's fonts rather than the ones the recipient sees.
 * `sandbox` without allow-scripts keeps a template from running anything.
 */
const MailTemplateTab = ({
  template,
  recipient,
  canSend,
}: {
  template: MailTemplateSummary;
  recipient: string | null;
  canSend: boolean;
}): JSX.Element => {
  const { Get, Post } = useApi();
  const { keycloak } = useAnketaAuth();

  const [html, setHtml] = useState<string | null>(null);
  const [subject, setSubject] = useState<string>(template.subject);
  const [replyTo, setReplyTo] = useState<string>(template.replyTo);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState<boolean>(false);
  const [sendResult, setSendResult] = useState<
    { ok: boolean; message: string } | null
  >(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      const resp = await Get<any>({
        path: `v2/admin/mail/templates/${template.id}/preview`,
        responseType: "json",
        token: keycloak?.token,
      });
      if (cancelled) return;
      const payload = payloadOf<{ html: string; subject: string; replyTo: string }>(
        resp,
      );
      if (!payload?.html) {
        setError("Vorschau konnte nicht geladen werden.");
        setLoading(false);
        return;
      }
      setHtml(payload.html);
      setSubject(payload.subject ?? template.subject);
      setReplyTo(payload.replyTo ?? template.replyTo);
      setLoading(false);
    };
    if (keycloak?.token) load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template.id, keycloak?.token]);

  const send = async () => {
    setSending(true);
    setSendResult(null);
    const resp = await Post<any>({
      path: `v2/admin/mail/templates/${template.id}/send`,
      value: {},
      responseType: "json",
      token: keycloak?.token,
    });
    const payload = payloadOf<{ ok: boolean; recipient?: string; error?: string }>(
      resp,
    );
    if (!payload) {
      setSendResult({ ok: false, message: "Der Versand konnte nicht ausgelöst werden." });
    } else if (payload.ok) {
      setSendResult({
        ok: true,
        message: `Testmail an ${payload.recipient} verschickt.`,
      });
    } else {
      setSendResult({
        ok: false,
        message: payload.error ?? "Der Versand ist fehlgeschlagen.",
      });
    }
    setSending(false);
  };

  return (
    <>
      <EuiSpacer size="m" />
      <EuiText size="s">
        <p>{template.description}</p>
        <p>
          <strong>Ausgelöst durch:</strong> {template.trigger}
        </p>
        <p>
          <strong>Betreff:</strong> {subject}
        </p>
        <p>
          <strong>Antwort an:</strong> <EuiCode>{replyTo}</EuiCode>
        </p>
      </EuiText>

      <EuiSpacer size="s" />

      <EuiFlexGroup alignItems="center" gutterSize="s" responsive={false}>
        <EuiFlexItem grow={false}>
          <EuiButton
            size="s"
            fill
            iconType="email"
            isLoading={sending}
            isDisabled={!canSend || loading}
            onClick={send}
          >
            {recipient
              ? `Testmail an ${recipient} senden`
              : "Testmail senden"}
          </EuiButton>
        </EuiFlexItem>
        {!canSend && (
          <EuiFlexItem grow={false}>
            <EuiText size="xs" color="subdued">
              Im Access-Token steht keine E-Mail-Adresse.
            </EuiText>
          </EuiFlexItem>
        )}
      </EuiFlexGroup>

      {sendResult && (
        <>
          <EuiSpacer size="s" />
          <EuiCallOut
            size="s"
            color={sendResult.ok ? "success" : "danger"}
            title={sendResult.message}
          />
        </>
      )}

      <EuiSpacer size="m" />

      {loading && <EuiLoadingSpinner size="l" />}
      {error && <EuiCallOut size="s" color="danger" title={error} />}
      {!loading && !error && html && (
        <iframe
          title={`Vorschau ${template.name}`}
          srcDoc={html}
          sandbox=""
          style={{
            width: "100%",
            height: "70vh",
            border: "1px solid rgba(128,128,128,0.3)",
            borderRadius: 4,
            // Mail templates assume a light client; without this the iframe is
            // transparent and the dark UI shows through the body gaps.
            background: "#ffffff",
          }}
        />
      )}
    </>
  );
};

/**
 * Admin > E-Mail. Shows the active mail transport, lets a superuser test it,
 * and renders every template the application can send.
 *
 * Test mails always go to the address in the caller's own access token - the
 * backend takes no recipient parameter, so this screen cannot be used to aim a
 * template at a customer.
 */
export const AdminMailTemplates = (): JSX.Element => {
  const { Get, Post } = useApi();
  const { keycloak } = useAnketaAuth();

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [setup, setSetup] = useState<MailSetupResponse | null>(null);
  const [verifying, setVerifying] = useState<boolean>(false);
  const [verifyResult, setVerifyResult] = useState<
    { ok: boolean; message: string } | null
  >(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    const resp = await Get<any>({
      path: "v2/admin/mail/setup",
      responseType: "json",
      token: keycloak?.token,
    });
    const payload = payloadOf<MailSetupResponse>(resp);
    if (!payload?.transport) {
      setError("Mail-Konfiguration konnte nicht geladen werden.");
      setLoading(false);
      return;
    }
    setSetup(payload);
    setLoading(false);
  };

  useEffect(() => {
    if (keycloak?.token) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keycloak?.token]);

  const verify = async () => {
    setVerifying(true);
    setVerifyResult(null);
    const resp = await Post<any>({
      path: "v2/admin/mail/setup/verify",
      value: {},
      responseType: "json",
      token: keycloak?.token,
    });
    const payload = payloadOf<{ ok: boolean; error?: string }>(resp);
    if (!payload) {
      setVerifyResult({ ok: false, message: "Der Test konnte nicht ausgeführt werden." });
    } else if (payload.ok) {
      setVerifyResult({
        ok: true,
        message:
          "Mail-Transport bereit - Anmeldung und Sendeberechtigung bestätigt.",
      });
    } else {
      setVerifyResult({
        ok: false,
        message: payload.error ?? "Der Mail-Transport hat die Anmeldung abgelehnt.",
      });
    }
    setVerifying(false);
  };

  if (loading) return <TableSkeleton />;
  if (error || !setup) {
    return <EuiCallOut color="danger" title={error ?? "Fehler"} />;
  }

  const { transport } = setup;
  // Env variable names are part of the label on purpose: this panel exists so
  // an admin can answer "which address does this send from" and then go edit
  // the right line in .env without guessing which of the three it is.
  const setupItems = [
    {
      title: "Transport",
      description:
        transport.mode === "graph" ? "Microsoft Graph (sendMail)" : "SMTP",
    },
    { title: "Endpunkt", description: transport.endpoint },
    ...(transport.encryption
      ? [{ title: "Verschlüsselung", description: transport.encryption }]
      : []),
    {
      title: "Absender (NODE_MAILER_FROM)",
      description: transport.fromHeader ? (
        <EuiText size="s">
          <EuiCode>{transport.fromHeader}</EuiCode>
          {transport.senderDisplay ? ` (angezeigt als "${transport.senderDisplay}")` : ""}
        </EuiText>
      ) : (
        <EuiText size="s" color="danger">
          nicht gesetzt
        </EuiText>
      ),
    },
    {
      title:
        transport.mode === "graph"
          ? `Versendendes Postfach (${transport.senderSource})`
          : `SMTP-Anmeldung (${transport.senderSource})`,
      description: (
        <EuiText size="s">
          <EuiCode>{transport.sender || "-"}</EuiCode>
        </EuiText>
      ),
    },
    {
      title: "Antwort an - Standard",
      description: (
        <EuiText size="s">
          <EuiCode>{transport.replyTo.value}</EuiCode> ({transport.replyTo.source})
        </EuiText>
      ),
    },
    {
      title: "Antwort an - Kundenbenachrichtigungen",
      description: (
        <EuiText size="s">
          <EuiCode>{transport.ticketReplyTo.value}</EuiCode> (
          {transport.ticketReplyTo.source})
        </EuiText>
      ),
    },
    {
      title: "Auswahl",
      description: transport.pinned
        ? "über NODE_MAILER_TRANSPORT festgelegt"
        : "automatisch anhand der Konfiguration",
    },
  ];

  const tabs: EuiTabbedContentTab[] = setup.templates.map((template) => ({
    id: template.id,
    name: template.name,
    content: (
      <MailTemplateTab
        template={template}
        recipient={setup.recipient}
        canSend={setup.canSend}
      />
    ),
  }));

  return (
    <>
      <EuiPanel paddingSize="m" hasShadow={false} hasBorder>
        <EuiFlexGroup alignItems="center" gutterSize="s" responsive={false}>
          <EuiFlexItem grow={false}>
            <EuiTitle size="xs">
              <h3>Mail-Setup</h3>
            </EuiTitle>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiBadge color={transport.mode === "graph" ? "primary" : "hollow"}>
              {transport.mode === "graph" ? "Graph" : "SMTP"}
            </EuiBadge>
          </EuiFlexItem>
        </EuiFlexGroup>

        <EuiSpacer size="s" />

        <EuiDescriptionList
          type="column"
          compressed
          listItems={setupItems}
          style={{ maxWidth: 720 }}
        />

        {!transport.fromMatchesSender && (
          <>
            <EuiSpacer size="s" />
            <EuiCallOut
              size="s"
              color="warning"
              title="From-Header und versendendes Postfach sind verschieden"
            >
              <p>
                Versendet wird über <EuiCode>{transport.sender || "-"}</EuiCode>,
                der From-Header lautet{" "}
                <EuiCode>{transport.fromHeader || "-"}</EuiCode>. Zwei Fälle:
              </p>
              <ul>
                <li>
                  Ist die Adresse ein <strong>Alias</strong> (Proxy-Adresse)
                  desselben Postfachs, wird der Versand akzeptiert - Exchange
                  schreibt den Absender aber auf die primäre Adresse des
                  Postfachs um, solange{" "}
                  <EuiCode>
                    Set-OrganizationConfig -SendFromAliasEnabled $true
                  </EuiCode>{" "}
                  nicht gesetzt ist. Bleibt der Alias erhalten, braucht dessen
                  Domain eigene SPF- und DKIM-Einträge in Exchange Online.
                </li>
                <li>
                  Gehört die Adresse zu einem <strong>anderen Postfach</strong>,
                  wird der Versand mit <EuiCode>5.7.60</EuiCode> abgelehnt, bis
                  eine Send-As-Berechtigung erteilt ist.
                </li>
              </ul>
            </EuiCallOut>
          </>
        )}

        <EuiSpacer size="m" />

        <EuiFlexGroup alignItems="center" gutterSize="s" responsive={false}>
          <EuiFlexItem grow={false}>
            <EuiButton
              size="s"
              iconType="check"
              isLoading={verifying}
              onClick={verify}
            >
              Verbindung testen
            </EuiButton>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiText size="xs" color="subdued">
              Prüft Anmeldung und Sendeberechtigung - es wird keine Mail
              verschickt.
            </EuiText>
          </EuiFlexItem>
        </EuiFlexGroup>

        {verifyResult && (
          <>
            <EuiSpacer size="s" />
            <EuiCallOut
              size="s"
              color={verifyResult.ok ? "success" : "danger"}
              title={verifyResult.message}
            />
          </>
        )}
      </EuiPanel>

      <EuiSpacer size="l" />

      <EuiTitle size="xs">
        <h3>Vorlagen</h3>
      </EuiTitle>
      <EuiText size="xs" color="subdued">
        <p>
          Vorschau mit Beispieldaten. Der Versand geht immer an die eigene
          Adresse aus dem Access-Token
          {setup.recipient ? ` (${setup.recipient})` : ""}.
        </p>
      </EuiText>

      <EuiSpacer size="s" />

      {tabs.length > 0 && (
        <EuiTabbedContent tabs={tabs} initialSelectedTab={tabs[0]} />
      )}
    </>
  );
};
